import * as React from "react";
import Container from "./styles";
import type { TypeIconV2Props } from "./IconTypes";

/**
 * **Icon (v2)** — a structural wrapper that centers a caller-supplied SVG inside a
 * fixed-size container, slightly larger than the icon to give it breathing room
 * (a buffer against focus rings, hover backgrounds, and button padding).
 *
 * The caller passes the SVG directly via `children` (or the `icon` alias). The SVG
 * should use `viewBox="0 0 24 24"` and `currentColor` fills — the component controls
 * only the rendered width/height and centering, never the viewBox or path-level fills.
 *
 * Sizes:
 * - `default` — 24×24px container, icon renders at 18×18px
 * - `small` — 20×20px container, icon renders at 15×15px
 *
 * Accessibility: decorative by default (`aria-hidden`). Pass `aria-label` to expose a
 * standalone icon — the container then gets `role="img"` and the label.
 *
 * @example
 * <Icon aria-label="Settings"><MySvg /></Icon>
 */
const Icon = React.forwardRef<HTMLSpanElement, TypeIconV2Props>(
  (
    { size = "default", children, icon, "aria-label": ariaLabel, ...rest },
    ref
  ) => {
    const content = children ?? icon;
    const ariaProps = ariaLabel
      ? { role: "img" as const, "aria-label": ariaLabel }
      : { "aria-hidden": true };

    return (
      <Container
        ref={ref}
        iconSize={size}
        data-qa-icon-v2={size}
        {...ariaProps}
        {...rest}
      >
        {content}
      </Container>
    );
  }
);

Icon.displayName = "IconV2";

export default Icon;
